import { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import { Heart, X, ShoppingBag, Trash2, ArrowRight } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { useCart } from '../contexts/CartContext';

const API = `${window.location.origin}/api`;

/**
 * Header wishlist heart icon + slide-in drawer.
 * - Authed: opens drawer with full wishlist (remove / add-to-cart / view all).
 * - Non-authed: opens drawer with login CTA.
 * Count refreshes every 60s + when drawer closes (after add-to-cart).
 */
export default function WishlistButton() {
  const { isAuthenticated } = useAuth();
  const { addToCart } = useCart();
  const [isOpen, setIsOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(false);

  const fetchWishlist = useCallback(async () => {
    if (!isAuthenticated) { setCount(0); setItems([]); return; }
    const token = localStorage.getItem('auth_token');
    if (!token) return;
    try {
      const r = await axios.get(`${API}/customer/wishlist`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const list = r.data || [];
      setItems(list);
      setCount(list.length);
    } catch {
      // silent — never break header
    }
  }, [isAuthenticated]);

  // Background count refresh
  useEffect(() => {
    fetchWishlist();
    const id = setInterval(fetchWishlist, 60000);
    return () => clearInterval(id);
  }, [fetchWishlist]);

  // Lock body scroll when drawer open + ESC to close
  useEffect(() => {
    if (!isOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e) => { if (e.key === 'Escape') setIsOpen(false); };
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener('keydown', onKey);
    };
  }, [isOpen]);

  const openDrawer = async () => {
    setIsOpen(true);
    if (isAuthenticated) {
      setLoading(true);
      await fetchWishlist();
      setLoading(false);
    }
  };

  const handleRemove = async (productId) => {
    const token = localStorage.getItem('auth_token');
    if (!token) return;
    try {
      await axios.delete(`${API}/customer/wishlist/${productId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setItems(prev => prev.filter(p => p.id !== productId));
      setCount(c => Math.max(0, c - 1));
      toast.success('Removed from wishlist');
    } catch {
      toast.error('Could not remove. Try again.');
    }
  };

  const handleAddToCart = (product) => {
    addToCart(product, 1, null);
    toast.success(`${product.name} added to cart`);
  };

  return (
    <>
      <button
        type="button"
        onClick={openDrawer}
        className="relative"
        data-testid="header-wishlist-button"
        aria-label="Wishlist"
      >
        <Heart className="w-5 h-5 text-white/80 hover:text-[#D4AF37] transition-colors" />
        {count > 0 && (
          <span
            className="absolute -top-2 -right-2 bg-[#D4AF37] text-black text-xs w-5 h-5 flex items-center justify-center font-medium"
            data-testid="wishlist-count-badge"
          >
            {count}
          </span>
        )}
      </button>

      {isOpen && createPortal(
        <div className="fixed inset-0 z-[60]" data-testid="wishlist-drawer-overlay">
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-black/70 backdrop-blur-sm"
            onClick={() => setIsOpen(false)}
            aria-hidden="true"
          />

          {/* Drawer */}
          <aside
            className="absolute inset-y-0 right-0 w-full sm:w-[420px] bg-[#0A0A0A] border-l border-[#D4AF37]/20 shadow-2xl flex flex-col animate-slide-in-right"
            data-testid="wishlist-drawer"
            role="dialog"
            aria-label="Wishlist"
          >
            {/* Header */}
            <div className="flex items-center justify-between px-6 py-5 border-b border-white/10">
              <div className="flex items-center gap-3">
                <Heart className="w-5 h-5 text-[#D4AF37]" />
                <h2 className="text-sm uppercase tracking-[0.2em] text-white font-sans">
                  Wishlist {count > 0 && <span className="text-[#D4AF37]">({count})</span>}
                </h2>
              </div>
              <button
                type="button"
                onClick={() => setIsOpen(false)}
                className="text-white/60 hover:text-white transition-colors"
                aria-label="Close wishlist"
                data-testid="close-wishlist-drawer"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Body */}
            <div className="flex-1 overflow-y-auto">
              {!isAuthenticated ? (
                <EmptyState
                  title="Sign in to see your wishlist"
                  body="Save your favorite scents and access them on any device."
                  ctaLabel="Login / Register"
                  ctaTo="/login?next=/account?tab=wishlist"
                  onClose={() => setIsOpen(false)}
                />
              ) : loading ? (
                <div className="px-6 py-10 text-center text-white/40 text-sm font-sans">
                  Loading your wishlist...
                </div>
              ) : items.length === 0 ? (
                <EmptyState
                  title="Your wishlist is empty"
                  body="Tap the heart on any product to save it here for later."
                  ctaLabel="Browse Scents"
                  ctaTo="/shop"
                  onClose={() => setIsOpen(false)}
                />
              ) : (
                <ul className="divide-y divide-white/5" data-testid="wishlist-items">
                  {items.map(product => (
                    <li
                      key={product.id}
                      className="flex gap-4 px-6 py-4"
                      data-testid={`wishlist-item-${product.id}`}
                    >
                      <Link
                        to={`/product/${product.id}`}
                        onClick={() => setIsOpen(false)}
                        className="block w-20 h-20 bg-[#141414] flex-shrink-0 overflow-hidden"
                      >
                        <img
                          src={product.image_url}
                          alt={product.name}
                          className="w-full h-full object-cover"
                        />
                      </Link>
                      <div className="flex-1 min-w-0 flex flex-col justify-between">
                        <div>
                          <Link
                            to={`/product/${product.id}`}
                            onClick={() => setIsOpen(false)}
                            className="block text-white text-sm font-sans hover:text-[#D4AF37] transition-colors line-clamp-2"
                          >
                            {product.name}
                          </Link>
                          <p className="text-white/40 text-xs font-sans mt-0.5">
                            ₱{product.base_price?.toLocaleString()}
                          </p>
                        </div>
                        <div className="flex items-center gap-2 mt-2">
                          <button
                            type="button"
                            onClick={() => handleAddToCart(product)}
                            className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.15em] px-3 py-1.5 bg-[#D4AF37] text-black hover:bg-[#E5C158] transition-colors font-sans"
                            data-testid={`wishlist-add-cart-${product.id}`}
                          >
                            <ShoppingBag className="w-3 h-3" />
                            Add to Cart
                          </button>
                          <button
                            type="button"
                            onClick={() => handleRemove(product.id)}
                            className="flex items-center justify-center w-7 h-7 text-white/40 hover:text-red-400 transition-colors"
                            aria-label="Remove from wishlist"
                            data-testid={`wishlist-remove-${product.id}`}
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            {/* Footer */}
            {isAuthenticated && items.length > 0 && (
              <div className="border-t border-white/10 px-6 py-4">
                <Link
                  to="/account?tab=wishlist"
                  onClick={() => setIsOpen(false)}
                  className="flex items-center justify-center gap-2 w-full py-3 border border-[#D4AF37]/30 text-[#D4AF37] text-xs uppercase tracking-[0.2em] font-sans hover:bg-[#D4AF37]/10 transition-colors"
                  data-testid="wishlist-view-all"
                >
                  View Full Wishlist
                  <ArrowRight className="w-3.5 h-3.5" />
                </Link>
              </div>
            )}
          </aside>
        </div>,
        document.body
      )}
    </>
  );
}

function EmptyState({ title, body, ctaLabel, ctaTo, onClose }) {
  return (
    <div className="flex flex-col items-center justify-center text-center px-8 py-16">
      <div className="w-16 h-16 rounded-full border border-[#D4AF37]/30 flex items-center justify-center mb-6">
        <Heart className="w-7 h-7 text-[#D4AF37]" />
      </div>
      <h3 className="text-lg text-white font-serif mb-2">{title}</h3>
      <p className="text-white/50 text-sm font-sans mb-6 max-w-xs">{body}</p>
      <Link
        to={ctaTo}
        onClick={onClose}
        className="inline-flex items-center gap-2 px-6 py-3 bg-[#D4AF37] text-black text-xs uppercase tracking-[0.2em] font-sans hover:bg-[#E5C158] transition-colors"
        data-testid="wishlist-empty-cta"
      >
        {ctaLabel}
        <ArrowRight className="w-3.5 h-3.5" />
      </Link>
    </div>
  );
}
